import axios from "axios";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import TopBar from "./Topbar";

import { notifyUser } from "../utils/notifyUser";

// Get backend URL from environment or use default
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InvestmentLimitsPage = () => {
    const navigate = useNavigate();
    const [isAccreditedInvestor, setIsAccreditedInvestor] = useState("");
    const [accreditedInvestorType, setAccreditedInvestorType] = useState("");
    const [netWorth, setNetWorth] = useState("");
    const [yearlyIncome, setYearlyIncome] = useState("");
    const [priorRegCfAmount, setPriorRegCfAmount] = useState("0");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if(!isAccreditedInvestor) {
            notifyUser(null, "error", "Please specify if you are an accredited investor.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if(isAccreditedInvestor === "true" && !accreditedInvestorType) {
            notifyUser(null, "error", "Please select your accredited investor type.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setIsSubmitting(true);

        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");

            const response = await axios.post(
                `${BACKEND_URL}/api/investment-limits`,
                {
                    email: user.email,
                    isAccreditedInvestor:
                        isAccreditedInvestor === "true" ? "Yes" : "No",
                    accreditedInvestorType: accreditedInvestorType || null,
                    netWorth: netWorth || null,
                    yearlyIncome: yearlyIncome || null,
                    priorRegCfAmount: priorRegCfAmount || "0",
                },
            );

            if(response.status === 200) {
                notifyUser(null, "success", "Investment profile completed successfully!", "toast", null, null, null, null, null, false, false, "top-center");

                // Update user in localStorage
                const updatedUser = {
                    ...user,
                    application_submitted: 1,
                    isProfileVerified: "true",
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));

                // Navigate to dashboard
                setTimeout(() => navigate("/dashboard"), 2000);
            }
        } catch(error) {
            console.error("Error saving investment limits:", error);
            notifyUser(null, "error", "Failed to save investment limits. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Format currency input
    const formatCurrency = (value) => {
        const numericValue = value.replace(/[^0-9]/g, "");
        return numericValue ? parseInt(numericValue).toLocaleString() : "";
    };

    const handleCurrencyChange = (value, setter) => {
        const formatted = formatCurrency(value);
        setter(formatted);
    };

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <TopBar />

            {/* Progress Bar */}
            <div className="bg-gray-100 p-4">
                <div className="max-w-6xl mx-auto">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">
                            Step 5 of 5
                        </span>
                        <span className="text-sm text-gray-600">100%</span>
                    </div>
                    <div className="w-full bg-gray-300 rounded-full h-2">
                        <div
                            className="bg-navy h-2 rounded-full transition-all duration-300"
                            style={{ width: "100%" }}
                        ></div>
                    </div>
                </div>
            </div>

            <div className="flex-grow flex items-center justify-center py-8 px-4">
                <div className="max-w-3xl w-full">
                    <div className="bg-white p-8 rounded-xl shadow-lg">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-navy mb-4">
                                Investment Information
                            </h1>
                            <p className="text-gray-600">
                                Please provide information about your investment
                                eligibility and financial status. This helps us
                                ensure compliance with securities regulations.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Accredited Investor Question */}
                            <div>
                                <label className="block text-lg font-medium text-navy mb-4">
                                    Are you an accredited investor? *
                                </label>
                                <div className="space-y-3">
                                    <label className="flex items-center space-x-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="accredited"
                                            value="true"
                                            checked={
                                                isAccreditedInvestor === "true"
                                            }
                                            onChange={(e) =>
                                                setIsAccreditedInvestor(
                                                    e.target.value,
                                                )
                                            }
                                            className="h-4 w-4 text-navy focus:ring-navy border-gray-300"
                                        />
                                        <span className="text-gray-700">
                                            Yes, I am an accredited investor
                                        </span>
                                    </label>
                                    <label className="flex items-center space-x-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="accredited"
                                            value="false"
                                            checked={
                                                isAccreditedInvestor === "false"
                                            }
                                            onChange={(e) =>
                                                setIsAccreditedInvestor(
                                                    e.target.value,
                                                )
                                            }
                                            className="h-4 w-4 text-navy focus:ring-navy border-gray-300"
                                        />
                                        <span className="text-gray-700">
                                            No, I am not an accredited investor
                                        </span>
                                    </label>
                                </div>

                                <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                                    <strong>Note:</strong> Accredited investors
                                    are individuals who meet specific income or
                                    net worth requirements as defined by SEC
                                    regulations.
                                </div>
                            </div>

                            {/* Accredited Investor Type */}
                            {isAccreditedInvestor === "true" && (
                                <div>
                                    <label className="block text-lg font-medium text-navy mb-4">
                                        Please select your accredited investor
                                        qualification: *
                                    </label>
                                    <div className="space-y-3">
                                        <label className="flex items-start space-x-3 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="accreditedType"
                                                value="net_worth_spouse_1_million"
                                                checked={
                                                    accreditedInvestorType ===
                                                    "net_worth_spouse_1_million"
                                                }
                                                onChange={(e) =>
                                                    setAccreditedInvestorType(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-4 w-4 text-navy focus:ring-navy border-gray-300 mt-0.5"
                                            />
                                            <span className="text-gray-700">
                                                Net worth exceeding $1 million
                                                (including spouse)
                                            </span>
                                        </label>
                                        <label className="flex items-start space-x-3 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="accreditedType"
                                                value="net_worth_individual_200k"
                                                checked={
                                                    accreditedInvestorType ===
                                                    "net_worth_individual_200k"
                                                }
                                                onChange={(e) =>
                                                    setAccreditedInvestorType(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-4 w-4 text-navy focus:ring-navy border-gray-300 mt-0.5"
                                            />
                                            <span className="text-gray-700">
                                                Individual income exceeding
                                                $200,000 in each of the last two
                                                years
                                            </span>
                                        </label>
                                        <label className="flex items-start space-x-3 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="accreditedType"
                                                value="series_7_65_82_license"
                                                checked={
                                                    accreditedInvestorType ===
                                                    "series_7_65_82_license"
                                                }
                                                onChange={(e) =>
                                                    setAccreditedInvestorType(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-4 w-4 text-navy focus:ring-navy border-gray-300 mt-0.5"
                                            />
                                            <span className="text-gray-700">
                                                Holder of Series 7, 65, or 82
                                                license
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Financial Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-navy mb-2">
                                        Estimated Net Worth (Optional)
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                            $
                                        </span>
                                        <input
                                            type="text"
                                            placeholder="0"
                                            value={netWorth}
                                            onChange={(e) =>
                                                handleCurrencyChange(
                                                    e.target.value,
                                                    setNetWorth,
                                                )
                                            }
                                            className="w-full pl-8 pr-3 py-3 border border-navy rounded-xl font-poppins focus:ring-2 focus:ring-navy focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-navy mb-2">
                                        Annual Income (Optional)
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                            $
                                        </span>
                                        <input
                                            type="text"
                                            placeholder="0"
                                            value={yearlyIncome}
                                            onChange={(e) =>
                                                handleCurrencyChange(
                                                    e.target.value,
                                                    setYearlyIncome,
                                                )
                                            }
                                            className="w-full pl-8 pr-3 py-3 border border-navy rounded-xl font-poppins focus:ring-2 focus:ring-navy focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Prior Regulation CF Investment Amount */}
                            <div>
                                <label className="block text-sm font-medium text-navy mb-2">
                                    Total amount invested in Regulation CF
                                    offerings in the last 12 months
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                        $
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="0"
                                        value={priorRegCfAmount}
                                        onChange={(e) =>
                                            handleCurrencyChange(
                                                e.target.value,
                                                setPriorRegCfAmount,
                                            )
                                        }
                                        className="w-full pl-8 pr-3 py-3 border border-navy rounded-xl font-poppins focus:ring-2 focus:ring-navy focus:border-transparent"
                                    />
                                </div>
                                <div className="mt-2 text-sm text-gray-600">
                                    This includes all crowdfunding investments
                                    made under SEC Regulation CF across all
                                    platforms.
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="pt-6">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-navy text-white py-4 px-6 rounded-xl font-bold text-lg hover:bg-navy-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isSubmitting
                                        ? "Completing Setup..."
                                        : "Complete Account Setup"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default InvestmentLimitsPage;
